import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Button, Paper } from "@mantine/core";
import BatchSummaryStats from "./BatchSummaryStats";
import BatchSummaryDetails from "./BatchSummaryDetails";
import BatchTransactionsFilters from "./components/BatchTransactionsFilters";
import BatchTransactionsTable from "./components/BatchTransactionsTable";
import BatchTransactionsSummaryFooter from "./components/BatchTransactionsSummaryFooter";
import TransactionReceiptModal from "./components/TransactionReceiptModal";
import useBatchTransactionsFilters from "./hooks/useBatchTransactionsFilters";
import useBatchTransactionsExport from "./hooks/useBatchTransactionsExport";
import { useBatchResume } from "./hooks/useBatchResume";
import {
  useGetBatchRecipients,
  useGetManagementBatchRecipients,
} from "../../../hooks/queries/useBulkUploadsQueries";
import FetchFeedback from "../../ui/CustomFetchFeedback/CustomFetchFeedback";
import CustomEmptyState from "../../ui/CustomEmptyState/CustomEmptyState";
import CustomNotificationBanner from "../../ui/CustomNotificationBanner/CustomNotificationBanner";

export default function BatchSummaryPage({ id, isSuperAdmin = false }) {
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);

  const { isIncomplete, onResumeBatch, showPrint, bannerTitle, bannerMessage } = useBatchResume(id, isSuperAdmin);

  const {
    apiFilters,
    pagination,
    setPagination,
    searchQuery,
    dateRange,
    status,
    setSearchQuery,
    setDateRange,
    setStatus,
  } = useBatchTransactionsFilters();

  const { handleExport, isExporting } = useBatchTransactionsExport(
    id,
    apiFilters,
  );

  const merchantRecipientsQuery = useGetBatchRecipients(id, apiFilters, {
    enabled: !isSuperAdmin,
  });
  const managementRecipientsQuery = useGetManagementBatchRecipients(id, apiFilters, {
    enabled: isSuperAdmin,
  });
  const {
    data: queryData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = isSuperAdmin ? managementRecipientsQuery : merchantRecipientsQuery;

  const onViewTransaction = useCallback((row) => {
    setSelectedRecipient(row);
    setModalOpened(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setModalOpened(false);
    setSelectedRecipient(null);
  }, []);

  const content = queryData?.content ?? [];
  const hasActiveFilters =
    !!searchQuery ||
    status !== "all" ||
    !!dateRange?.startDate ||
    !!dateRange?.endDate;

  const isEmpty = !isLoading && !isError && content.length === 0;
  const isTrueEmpty = isEmpty && !hasActiveFilters;
  const isFilteredEmpty = isEmpty && hasActiveFilters;
  const filtersDisabled = isLoading || isError || isTrueEmpty;

  const renderContent = () => {
    if (isError) {
      return (
        <FetchFeedback
          message="Failed to load transactions. Please try again."
          onRetry={refetch}
          fullHeight={false}
          compact
        />
      );
    }

    if (isTrueEmpty) {
      return (
        <CustomEmptyState
          primary="No transactions yet"
          secondary="Transactions for this batch will appear here once processing begins."
          height={250}
        />
      );
    }

    if (isFilteredEmpty) {
      return (
        <CustomEmptyState
          primary="No transactions match your filters"
          secondary="Try adjusting your search or date range."
          height={250}
        />
      );
    }

    return (
      <>
        <BatchTransactionsTable
          data={content}
          isLoading={isLoading}
          isFetching={isFetching}
          pagination={pagination}
          totalPages={queryData?.totalPages ?? 0}
          totalElements={queryData?.totalElements ?? 0}
          onPaginationChange={setPagination}
          onViewTransaction={onViewTransaction}
          hideActions={isIncomplete}
        />
        <BatchTransactionsSummaryFooter
          subtotal={queryData?.recipientsSummation?.amount}
          totalCharges={queryData?.recipientsSummation?.charges}
          grandTotal={queryData?.recipientsSummation?.totalAmount}
          isLoading={isLoading}
          isError={isError}
        />
      </>
    );
  };

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {isIncomplete && (
        <CustomNotificationBanner
          variant="warning"
          withCloseButton={false}
          title={bannerTitle}
          message={
            <div className="flex flex-col gap-2">
              <span>{bannerMessage}</span>
              <div>
                <Button size="xs" variant="filled" color="warning" onClick={onResumeBatch}>
                  Resume Batch
                </Button>
              </div>
            </div>
          }
        />
      )}
      <BatchSummaryStats id={id} isSuperAdmin={isSuperAdmin} />
      <BatchSummaryDetails id={id} isSuperAdmin={isSuperAdmin} />
      <Paper shadow="xs" p={{ base: "sm", sm: "xl" }}>
        <div className="flex flex-col gap-4">
          <BatchTransactionsFilters
            filters={{
              searchQuery,
              setSearchQuery,
              dateRange,
              setDateRange,
              status,
              setStatus,
            }}
            exportProps={{ handleExport, isExporting }}
            disabled={filtersDisabled}
          />
          {renderContent()}
        </div>
      </Paper>

      <TransactionReceiptModal
        recipientRow={selectedRecipient}
        opened={modalOpened}
        onClose={onCloseModal}
        showPrint={showPrint}
      />
    </div>
  );
}

BatchSummaryPage.propTypes = {
  id: PropTypes.string,
  isSuperAdmin: PropTypes.bool,
};
